import React, { useMemo } from "react";
import {
  FileText,
  FileCheck,
  FileX,
  IndianRupee,
  Clock3,
  AlertCircle,
  CheckCircle2,
  FilePlus2,
} from "lucide-react";
import { isInFilter } from "../utils/dateFilter";

const FILTER_TABS = ["Today", "Yesterday", "Weekly", "Monthly", "All Time", "Custom Date"];

const PAYMENT_BAR = [
  { key: "Paid", color: "#22c55e", tab: "Paid invoice" },
  { key: "Pending", color: "#facc15", tab: "Active invoice" },
  { key: "Overdue", color: "#ef4444", tab: "Overdue invoice" },
];

export function isInvoiceActive(dpstatus) {
  return String(dpstatus ?? "Published").trim().toLowerCase() !== "draft";
}

export function outstandingAmount(inv) {
  return (
    parseFloat(inv?.total_pending ?? inv?.pending ?? inv?.adjusted_pending ?? 0) || 0
  );
}

export function grandTotalAmount(inv) {
  return parseFloat(inv?.total_grand_total ?? inv?.grand_total ?? 0) || 0;
}

export function getInvoicePaymentStatus(invoice) {
  const today = new Date().toISOString().split("T")[0];
  const pending = outstandingAmount(invoice);
  const grand = grandTotalAmount(invoice);

  if (grand <= 0) return "No amount";
  if (pending === 0) return "Paid";

  const dueDate = invoice?.due_date;
  if (!dueDate || dueDate === "0000-00-00") return "Pending";

  try {
    if (new Date(dueDate).toISOString().split("T")[0] < today) {
      return "Overdue";
    }
  } catch {
    return "Pending";
  }

  return "Pending";
}

function invoiceActivityDate(inv) {
  const raw =
    inv?.createdAt || inv?.invoice_date || inv?.created_range?.to || inv?.created_range?.from;
  const parsed = raw ? new Date(raw) : null;
  return parsed && !isNaN(parsed.getTime()) ? parsed : null;
}

function getFilterLabel(filter) {
  if (typeof filter === "string") return filter;
  if (filter?.type === "Custom Date" && filter.from && filter.to) {
    return `${filter.from} to ${filter.to}`;
  }
  return "Selected period";
}

function formatCurrency(value) {
  const amount = Number(value) || 0;
  return `₹${amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

const SummaryDateFilter = ({ value, onChange }) => {
  const [activeTab, setActiveTab] = React.useState(
    typeof value === "string" ? value : value?.type === "Custom Date" ? "Custom Date" : "All Time"
  );
  const [showCustom, setShowCustom] = React.useState(activeTab === "Custom Date");
  const [tempRange, setTempRange] = React.useState({
    from: value?.from || "",
    to: value?.to || "",
  });

  const handleTabClick = (tab) => {
    if (tab === "Custom Date") {
      setActiveTab("Custom Date");
      setShowCustom(true);
      return;
    }
    setActiveTab(tab);
    setShowCustom(false);
    onChange(tab);
  };

  const handleApplyCustom = () => {
    if (!tempRange.from || !tempRange.to || tempRange.from > tempRange.to) return;
    onChange({ type: "Custom Date", from: tempRange.from, to: tempRange.to });
    setShowCustom(false);
  };

  return (
    <div className="w-full sm:w-auto sm:min-w-[280px]">
      <div className="flex items-center gap-1 dash-filter-scroll rounded-xl border border-line bg-surface-2 p-1">
        {FILTER_TABS.map((tab) => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => handleTabClick(tab)}
              className={`shrink-0 rounded-lg px-2.5 py-1.5 text-[11px] sm:text-xs font-semibold transition-colors whitespace-nowrap ${
                isActive
                  ? "bg-amber-500 text-white shadow-sm"
                  : "text-ink-muted hover:bg-white/10 hover:text-ink"
              }`}
            >
              {tab}
            </button>
          );
        })}
      </div>

      {showCustom && (
        <div className="mt-2 rounded-xl border border-dashed border-line-strong bg-surface-2 p-2.5">
          <div className="flex flex-col sm:flex-row gap-2">
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-ink-secondary flex-1">
              From
              <input
                type="date"
                value={tempRange.from}
                onChange={(e) => setTempRange((prev) => ({ ...prev, from: e.target.value }))}
                className="rounded-lg border border-line bg-surface text-ink px-2 py-1.5 text-xs outline-none focus:border-amber-400 [color-scheme:dark]"
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-ink-secondary flex-1">
              To
              <input
                type="date"
                value={tempRange.to}
                onChange={(e) => setTempRange((prev) => ({ ...prev, to: e.target.value }))}
                className="rounded-lg border border-line bg-surface text-ink px-2 py-1.5 text-xs outline-none focus:border-amber-400 [color-scheme:dark]"
              />
            </label>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowCustom(false);
                if (typeof value === "string") setActiveTab(value);
              }}
              className="rounded-lg border border-line px-3 py-1 text-xs font-semibold text-ink-secondary hover:bg-white/10"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApplyCustom}
              disabled={!tempRange.from || !tempRange.to || tempRange.from > tempRange.to}
              className="rounded-lg bg-amber-500 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, hint, tone = "default", icon: Icon, active, onClick }) => {
  const toneClass =
    tone === "success"
      ? "text-lime-300"
      : tone === "warn"
        ? "text-amber-300"
        : tone === "danger"
          ? "text-rose-300"
          : "text-ink";

  const Tag = onClick ? "button" : "div";

  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`rounded-lg border px-3 py-2 min-w-[118px] text-left transition-all ${
        active
          ? "border-amber-500 bg-white/10 ring-2 ring-amber-500/30 shadow-sm"
          : "border-line bg-white/5 hover:border-amber-500/40 hover:bg-white/10"
      } ${onClick ? "cursor-pointer" : ""}`}
      aria-pressed={onClick ? active : undefined}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-[11px] uppercase tracking-wide text-ink-muted font-semibold">{label}</p>
        {Icon ? <Icon size={14} className="text-ink-muted shrink-0" /> : null}
      </div>
      <p className={`text-lg font-bold ${toneClass}`}>{value}</p>
      {hint ? <p className="text-[11px] text-ink-muted mt-0.5">{hint}</p> : null}
    </Tag>
  );
};

export default function InvoicePageSummary({
  invoices = [],
  period = "All Time",
  onPeriodChange,
  lifecycleFilter = "all",
  onLifecycleFilterChange,
  paymentFilter = "All invoice",
  onPaymentFilterChange,
}) {
  const summary = useMemo(() => {
    const periodInvoices = (Array.isArray(invoices) ? invoices : []).filter((inv) => {
      const created = invoiceActivityDate(inv);
      if (!created) return period === "All Time";
      return isInFilter(created, period);
    });

    const published = periodInvoices.filter((inv) => isInvoiceActive(inv.dpstatus));
    const drafts = periodInvoices.filter((inv) => !isInvoiceActive(inv.dpstatus));

    const countByPayment = (status) =>
      published.filter((inv) => getInvoicePaymentStatus(inv) === status).length;

    const paidCount = countByPayment("Paid");
    const pendingCount = countByPayment("Pending");
    const overdueCount = countByPayment("Overdue");

    let paidAmount = 0;
    let pendingAmount = 0;
    let overdueAmount = 0;
    let totalReceivables = 0;

    published.forEach((inv) => {
      const outstanding = outstandingAmount(inv);
      const collected = Math.max(0, grandTotalAmount(inv) - outstanding);
      paidAmount += collected;
      totalReceivables += outstanding;

      if (outstanding <= 0) return;

      const paymentStatus = getInvoicePaymentStatus(inv);
      if (paymentStatus === "Overdue") overdueAmount += outstanding;
      else if (paymentStatus === "Pending") pendingAmount += outstanding;
    });

    const paymentTotal = paidAmount + pendingAmount + overdueAmount || 1;
    const activeCount = published.length;
    const inactiveCount = drafts.length;
    const total = periodInvoices.length;
    const statusTotal = total || 1;

    const addedToday = (Array.isArray(invoices) ? invoices : []).filter((inv) => {
      const created = invoiceActivityDate(inv);
      return created && isInFilter(created, "Today");
    }).length;

    const paymentCounts = PAYMENT_BAR.map((item) => ({
      ...item,
      count:
        item.key === "Paid"
          ? paidCount
          : item.key === "Pending"
            ? pendingCount
            : overdueCount,
    }));

    return {
      filterLabel: getFilterLabel(period),
      total,
      activeCount,
      inactiveCount,
      activePct: Math.round((activeCount / statusTotal) * 100),
      inactivePct: Math.round((inactiveCount / statusTotal) * 100),
      addedToday,
      paidCount,
      pendingCount,
      overdueCount,
      paidAmount,
      pendingAmount,
      overdueAmount,
      totalReceivables,
      paymentTotal,
      paymentCounts,
      paidPct: Math.round((paidAmount / paymentTotal) * 100),
      pendingPct: Math.round((pendingAmount / paymentTotal) * 100),
      overduePct: Math.round((overdueAmount / paymentTotal) * 100),
    };
  }, [invoices, period]);

  const switchLifecycle = (next) => {
    if (onLifecycleFilterChange) onLifecycleFilterChange(next);
  };

  const switchPayment = (tab) => {
    if (onPaymentFilterChange) onPaymentFilterChange(tab);
  };

  return (
    <section className="invoice-page-summary mb-4">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-base sm:text-lg font-bold text-ink">Invoice Summary</h2>
          <p className="text-xs sm:text-sm text-ink-muted">
            Published vs draft invoices and payment health — {summary.filterLabel}
          </p>
        </div>
        <SummaryDateFilter value={period} onChange={onPeriodChange} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 mb-3">
        <div className="rounded-xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 to-yellow-500/10 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-amber-300 shadow-sm">
              <FileText size={16} />
            </div>
            <h3 className="text-sm sm:text-base font-bold text-ink">Lifecycle</h3>
          </div>
          <div className="flex gap-2 dash-metric-scroll dash-metric-scroll--invoices pb-1">
            <MetricCard
              label="Total Invoices"
              value={summary.total}
              hint="In selected period"
              icon={FileText}
              active={lifecycleFilter === "all"}
              onClick={() => switchLifecycle("all")}
            />
            <MetricCard
              label="Active"
              value={summary.activeCount}
              hint={`Published · ${summary.activePct}%`}
              icon={FileCheck}
              tone="success"
              active={lifecycleFilter === "active"}
              onClick={() => switchLifecycle("active")}
            />
            <MetricCard
              label="Inactive"
              value={summary.inactiveCount}
              hint={`Draft · ${summary.inactivePct}%`}
              icon={FileX}
              tone={summary.inactiveCount > 0 ? "danger" : "default"}
              active={lifecycleFilter === "inactive"}
              onClick={() => switchLifecycle("inactive")}
            />
            <MetricCard
              label="Added Today"
              value={summary.addedToday}
              hint="New invoices today"
              icon={FilePlus2}
              tone={summary.addedToday > 0 ? "warn" : "default"}
            />
          </div>
        </div>

        <div className="rounded-xl border border-yellow-500/20 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-yellow-300 shadow-sm">
              <IndianRupee size={16} />
            </div>
            <h3 className="text-sm sm:text-base font-bold text-ink">Payment</h3>
          </div>
          <div className="flex gap-2 dash-metric-scroll dash-metric-scroll--invoices pb-1">
            <MetricCard
              label="Receivables"
              value={formatCurrency(summary.totalReceivables)}
              hint="Outstanding balance"
              icon={IndianRupee}
              tone={summary.totalReceivables > 0 ? "warn" : "default"}
            />
            <MetricCard
              label="Paid"
              value={summary.paidCount}
              hint={formatCurrency(summary.paidAmount)}
              icon={CheckCircle2}
              tone="success"
              active={paymentFilter === "Paid invoice"}
              onClick={() => switchPayment("Paid invoice")}
            />
            <MetricCard
              label="Pending"
              value={summary.pendingCount}
              hint={formatCurrency(summary.pendingAmount)}
              icon={Clock3}
              tone={summary.pendingCount > 0 ? "warn" : "default"}
              active={paymentFilter === "Active invoice"}
              onClick={() => switchPayment("Active invoice")}
            />
            <MetricCard
              label="Overdue"
              value={summary.overdueCount}
              hint={formatCurrency(summary.overdueAmount)}
              icon={AlertCircle}
              tone={summary.overdueCount > 0 ? "danger" : "default"}
              active={paymentFilter === "Overdue invoice"}
              onClick={() => switchPayment("Overdue invoice")}
            />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4 mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          Active vs inactive
        </p>
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
          {summary.activeCount > 0 && (
            <button
              type="button"
              title={`Active (Published): ${summary.activeCount}`}
              onClick={() => switchLifecycle("active")}
              className="h-full transition-opacity hover:opacity-80"
              style={{
                width: `${(summary.activeCount / (summary.total || 1)) * 100}%`,
                backgroundColor: "#22c55e",
                minWidth: "4px",
              }}
              aria-label={`Active ${summary.activeCount}`}
            />
          )}
          {summary.inactiveCount > 0 && (
            <button
              type="button"
              title={`Inactive (Draft): ${summary.inactiveCount}`}
              onClick={() => switchLifecycle("inactive")}
              className="h-full transition-opacity hover:opacity-80"
              style={{
                width: `${(summary.inactiveCount / (summary.total || 1)) * 100}%`,
                backgroundColor: "#94a3b8",
                minWidth: "4px",
              }}
              aria-label={`Inactive ${summary.inactiveCount}`}
            />
          )}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-600">
          <button
            type="button"
            onClick={() => switchLifecycle("active")}
            className={`inline-flex items-center gap-1 hover:text-slate-900 ${
              lifecycleFilter === "active" ? "font-bold text-slate-900" : ""
            }`}
          >
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
            Active / Published ({summary.activeCount})
          </button>
          <button
            type="button"
            onClick={() => switchLifecycle("inactive")}
            className={`inline-flex items-center gap-1 hover:text-slate-900 ${
              lifecycleFilter === "inactive" ? "font-bold text-slate-900" : ""
            }`}
          >
            <span className="inline-block w-2 h-2 rounded-full bg-slate-400" />
            Inactive / Draft ({summary.inactiveCount})
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4 mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          Payment breakdown
        </p>
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
          {summary.paidAmount > 0 && (
            <button
              type="button"
              title={`Paid: ${formatCurrency(summary.paidAmount)}`}
              onClick={() => switchPayment("Paid invoice")}
              className="h-full transition-opacity hover:opacity-80"
              style={{
                width: `${summary.paidPct}%`,
                backgroundColor: "#22c55e",
                minWidth: "4px",
              }}
            />
          )}
          {summary.pendingAmount > 0 && (
            <button
              type="button"
              title={`Pending: ${formatCurrency(summary.pendingAmount)}`}
              onClick={() => switchPayment("Active invoice")}
              className="h-full transition-opacity hover:opacity-80"
              style={{
                width: `${summary.pendingPct}%`,
                backgroundColor: "#facc15",
                minWidth: "4px",
              }}
            />
          )}
          {summary.overdueAmount > 0 && (
            <button
              type="button"
              title={`Overdue: ${formatCurrency(summary.overdueAmount)}`}
              onClick={() => switchPayment("Overdue invoice")}
              className="h-full transition-opacity hover:opacity-80"
              style={{
                width: `${summary.overduePct}%`,
                backgroundColor: "#ef4444",
                minWidth: "4px",
              }}
            />
          )}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-600">
          {summary.paymentCounts.map((item) => (
            <button
              key={`${item.key}-legend`}
              type="button"
              onClick={() => switchPayment(item.tab)}
              className={`inline-flex items-center gap-1 hover:text-slate-900 ${
                paymentFilter === item.tab ? "font-bold text-slate-900" : ""
              }`}
            >
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              {item.key} ({item.count})
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-slate-600">
        <div className="flex items-start gap-2 rounded-lg bg-white border border-amber-100 px-3 py-2">
          <FileCheck size={14} className="text-emerald-600 mt-0.5 shrink-0" />
          <span>
            {summary.activeCount > 0
              ? `${summary.activeCount} published invoice(s) are active in this period.`
              : "No published invoices in this period."}
          </span>
        </div>
        <div className="flex items-start gap-2 rounded-lg bg-white border border-slate-200 px-3 py-2">
          <FileX size={14} className="text-slate-500 mt-0.5 shrink-0" />
          <span>
            {summary.inactiveCount > 0
              ? `${summary.inactiveCount} draft invoice(s) are inactive until published.`
              : "No draft invoices in this period."}
          </span>
        </div>
        <div className="flex items-start gap-2 rounded-lg bg-white border border-red-100 px-3 py-2">
          <AlertCircle size={14} className="text-red-500 mt-0.5 shrink-0" />
          <span>
            {summary.overdueCount > 0
              ? `${summary.overdueCount} overdue invoice(s) totalling ${formatCurrency(summary.overdueAmount)}.`
              : "No overdue invoices in this period."}
          </span>
        </div>
      </div>
    </section>
  );
}
